import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { BulkApproveModal } from 'src/components/payments-ops/payments-review/BulkApproveModal'
import { PaymentsReviewToolbar } from 'src/components/payments-ops/payments-review/PaymentsReviewToolbar'
import { PendingPaymentsTable } from 'src/components/payments-ops/payments-review/PendingPaymentsTable'
import { ReviewPaymentModal } from 'src/components/payments-ops/payments-review/ReviewPaymentModal'
import { AdminFetchPendingPayments } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PendingPayment } from 'src/interfaces/paymentsReview'
import { getPendingPayments, getPendingPaymentsError } from 'src/selectors/paymentsReview'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { addS } from 'src/utils/baseUtils'
import {
  filterPendingPayments,
  listPartners,
  summarizePayments,
} from 'src/utils/paymentsOps/pendingPayments'

const PaymentsReviewPageComponent = () => {
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const pending = useSelector(getPendingPayments)
  const listError = useSelector(getPendingPaymentsError)
  // Bulk approve loops `POST /earnings-review/:id/decision` — `payments_review_decide`, exactly
  // like one decision. `payments_review_bulk_approve` guards no endpoint (spec §2.3).
  const canDecide = useHasPermission(InternalPermission.PaymentsReviewDecide)

  const [search, setSearch] = useState<string>('')
  const [partner, setPartner] = useState<string | null>(null)
  const [reviewingPayment, setReviewingPayment] = useState<PendingPayment | null>(null)
  const [isBulkOpen, setIsBulkOpen] = useState<boolean>(false)
  const [bulkBatch, setBulkBatch] = useState<PendingPayment[]>([])
  const [refreshToken, setRefreshToken] = useState<number>(0)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchPendingPayments.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken])

  const visibleRows = useMemo(() => {
    return filterPendingPayments(pending, { search, partner })
  }, [partner, pending, search])

  const partners = useMemo(() => {
    return listPartners(pending)
  }, [pending])

  const summary = useMemo(() => {
    return summarizePayments(visibleRows)
  }, [visibleRows])

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // Bulk approve acts on exactly what the user saw when they clicked: the batch is frozen here,
  // so the results view still lists it after the queue refetches and shrinks.
  const handleOpenBulk = useCallback(() => {
    setBulkBatch(visibleRows)
    setIsBulkOpen(true)
  }, [visibleRows])

  const handleCloseBulk = useCallback(() => {
    setIsBulkOpen(false)
  }, [])

  const handleCloseReview = useCallback(() => {
    setReviewingPayment(null)
  }, [])

  return (
    <div
      data-testid="payments-review-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="Payments Review"
        description={`${summary.count} pending ${addS(
          summary.count,
          'payment',
        )} · ${formatCentsAsUsd(summary.totalCents)} total`}
      />
      <PaymentsReviewToolbar
        search={search}
        partner={partner}
        partners={partners}
        canBulkApprove={visibleRows.length > 0}
        showBulkApprove={canDecide}
        onSearchChange={setSearch}
        onPartnerChange={setPartner}
        onRefresh={handleRefresh}
        onBulkApprove={handleOpenBulk}
      />
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <PendingPaymentsTable rows={visibleRows} onReview={setReviewingPayment} />
      <ReviewPaymentModal payment={reviewingPayment} onClose={handleCloseReview} />
      <BulkApproveModal isOpen={isBulkOpen} payments={bulkBatch} onClose={handleCloseBulk} />
    </div>
  )
}

export const PaymentsReviewPage = memo(PaymentsReviewPageComponent)
